import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, FileDown, Hash, Truck, Package, Home } from "lucide-react";
import { api, API } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import { fmtPrice } from "@/lib/format";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const TRACKER_ICONS = [Package, Truck, Home];

export default function PaymentSuccess() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const code = params.get("code");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId || !code) {
      setLoading(false);
      return;
    }
    const fetchOrder = () =>
      api
        .get(`/orders/${orderId}?code=${code}`)
        .then((r) => setData(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    fetchOrder();
    // Suivi en temps réel : relecture du statut toutes les 30 s
    const timer = setInterval(fetchOrder, 30000);
    return () => clearInterval(timer);
  }, [orderId, code]);

  const order = data?.order;
  // Étape vivante du suivi : 0 = commandée, 1 = expédiée, 2 = livrée
  const stepIndex = order?.status === "livree" ? 2 : order?.status === "expediee" ? 1 : 0;
  const orderDate = order?.created_at
    ? new Date(order.created_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", {
        year: "numeric", month: "long", day: "numeric",
      })
    : "";

  return (
    <main className="mx-auto max-w-2xl px-4 pt-32 pb-24 sm:px-8">
      <Seo title="Confirmation | NordiGuard" description="Confirmation de commande NordiGuard" />
      {loading ? (
        <div data-testid="payment-checking" className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
          <Loader2 size={40} className="animate-spin text-sky-400" />
          <p className="text-sm text-slate-400">{t("success.checking")}</p>
        </div>
      ) : data?.order ? (
        <div data-testid="payment-success">
          {/* Titre */}
          <motion.div {...fadeUp(0)} className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sky-400">{t("success.eyebrow")}</p>
            <h1 className="font-display mt-4 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
              {t("success.title1")}
              <br />
              <span className="text-glacier-gradient">{t("success.title2")}</span>
            </h1>
          </motion.div>

          {/* Suivi de commande */}
          <motion.div {...fadeUp(0.12)} className="mt-10" data-testid="success-tracker">
            <div className="flex items-center">
              {t("success.tracker").map((label, i) => {
                const Icon = TRACKER_ICONS[i];
                const done = i <= stepIndex;
                return (
                  <div key={label} className={`flex items-center ${i < 2 ? "flex-1" : ""}`}>
                    <div className="flex flex-col items-center gap-2">
                      <span
                        className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all ${
                          done
                            ? "border-sky-400 bg-sky-400 text-slate-950 shadow-[0_0_24px_rgba(56,189,248,0.5)]"
                            : "border-slate-700 bg-slate-900 text-slate-500"
                        }`}
                        data-testid={`tracker-step-${i}`}
                      >
                        {done ? <Check size={18} strokeWidth={3} /> : <Icon size={17} />}
                      </span>
                      <span className={`text-xs font-semibold ${done ? "text-slate-100" : "text-slate-500"}`}>{label}</span>
                    </div>
                    {i < 2 && (
                      <div className="relative mx-3 mb-6 h-px flex-1 bg-slate-800">
                        {i < stepIndex && <div className="absolute inset-y-0 left-0 w-full bg-sky-400" />}
                        {i === stepIndex && <div className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-sky-400 to-transparent" />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Suivi : numéro de tracking + note de mise à jour auto */}
          {order?.tracking_number && (
            <motion.div {...fadeUp(0.18)} className="glacial-card mt-5 flex items-center justify-between gap-4 p-5" data-testid="success-tracking">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("success.tracking")}</span>
              <span className="font-mono text-sm font-extrabold tracking-widest text-sky-300">{order.tracking_number}</span>
            </motion.div>
          )}
          <p className="mt-4 text-center text-[11px] text-slate-600">{t("success.liveNote")}</p>
          <motion.div {...fadeUp(0.22)} className="glacial-card mt-10 overflow-hidden">
            <img
              src="/images/preparation-commande.png"
              alt={lang === "en" ? "Your NordiGuard set being packed" : "Votre ensemble NordiGuard en cours d'emballage"}
              className="aspect-[16/9] w-full object-cover"
              data-testid="success-image"
            />
          </motion.div>

          {/* Salutation */}
          <motion.div {...fadeUp(0.3)} className="mt-10">
            <p className="font-display text-xl font-bold text-slate-50" data-testid="success-greeting">
              {t("success.greeting", { name: order?.name || "" })}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{t("success.intro")}</p>
          </motion.div>

          {/* Instructions Interac */}
          {order?.payment_method === "interac" && (
            <motion.div
              {...fadeUp(0.34)}
              className="mt-8 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-7"
              data-testid="success-interac-instructions"
            >
              <h2 className="font-display text-lg font-bold text-amber-300">{t("success.interacTitle")}</h2>
              <ol className="mt-4 space-y-3 text-sm leading-relaxed text-slate-200">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[11px] font-extrabold text-slate-950">1</span>
                  <span>
                    {t("success.interacStep1")}{" "}
                    <strong className="text-amber-200">{order?.total != null && fmtPrice(order.total, lang)}</strong>{" "}
                    {lang === "en" ? "to" : "à"}{" "}
                    <strong className="text-amber-200">payments@nordiguard.com</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[11px] font-extrabold text-slate-950">2</span>
                  <span>
                    {t("success.interacStep2")}{" "}
                    <strong className="font-mono tracking-widest text-amber-200">{order?.confirmation_code}</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[11px] font-extrabold text-slate-950">3</span>
                  <span>{t("success.interacStep3")}</span>
                </li>
              </ol>
            </motion.div>
          )}

          {/* Code de confirmation + facture */}
          {order?.confirmation_code && (
            <motion.div {...fadeUp(0.38)} className="glacial-card mt-8 p-7 text-center" data-testid="success-confirmation-code">
              <p className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
                <Hash size={13} /> {t("success.code")}
              </p>
              <p className="font-display mt-3 text-3xl font-extrabold tracking-[0.25em] text-slate-50 sm:text-4xl">
                {order.confirmation_code}
              </p>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">{t("success.codeNote")}</p>
              <a
                href={`${API}/orders/${order.id}/invoice?code=${code}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="success-invoice-download"
                className="group mt-5 inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 px-6 py-3 text-sm font-bold text-sky-300 transition-all duration-300 hover:bg-sky-500/20 hover:text-sky-200"
              >
                <FileDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                {t("success.invoiceCta")}
              </a>
            </motion.div>
          )}

          {/* Prochaines étapes */}
          <motion.div {...fadeUp(0.46)} className="mt-10">
            <h2 className="font-display text-lg font-bold text-slate-50">{t("success.nextTitle")}</h2>
            <ul className="mt-4 space-y-3.5">
              {t("success.next").map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {s}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Résumé de commande */}
          <motion.div {...fadeUp(0.54)} className="glacial-card mt-10 p-7" data-testid="success-summary">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">{t("success.summaryTitle")}</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <dt className="text-slate-500">{t("success.orderNumber")}</dt>
                <dd className="font-mono font-bold tracking-widest text-sky-300" data-testid="success-order-number">
                  {order?.confirmation_code}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-slate-500">{t("success.orderDate")}</dt>
                <dd className="font-semibold text-slate-200" data-testid="success-order-date">{orderDate}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-slate-500">{lang === "en" ? "Vehicle" : "Véhicule"}</dt>
                <dd className="text-right font-semibold text-slate-200">{order?.vehicle}</dd>
              </div>
            </dl>
            <div className="mt-5 border-t border-slate-800 pt-5">
              <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">{t("success.totalTitle")}</h3>
              <dl className="mt-3 space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">
                    NordiGuard Sur-Mesure 3D × {order?.quantity || 1}
                  </dt>
                  <dd className="font-semibold text-slate-200">{order?.subtotal != null && fmtPrice(order.subtotal, lang)}</dd>
                </div>
                {(order?.tax_total || 0) > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500">{t("checkout.taxes")}</dt>
                    <dd className="font-semibold text-slate-200">{fmtPrice(order.tax_total, lang)}</dd>
                  </div>
                )}
                <div className="flex items-baseline justify-between border-t border-slate-800 pt-3">
                  <dt className="text-sm font-semibold text-slate-300">Total</dt>
                  <dd className="font-display text-2xl font-extrabold text-slate-50" data-testid="success-total">
                    {order?.total != null && fmtPrice(order.total, lang)}
                  </dd>
                </div>
              </dl>
            </div>
          </motion.div>

          <motion.div {...fadeUp(0.62)} className="mt-10 text-center">
            <Link
              to="/"
              data-testid="success-home-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.5)]"
            >
              {t("success.homeCta")}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      ) : (
        <div data-testid="payment-pending" className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
          <p className="text-sm text-slate-400">{t("success.notFound")}</p>
          <Link to="/contact" className="text-sm font-semibold text-sky-400 underline underline-offset-4">
            {t("nav.contact")}
          </Link>
        </div>
      )}
    </main>
  );
}
